import {apiClient} from '../utils/apiClient';

export interface GPXTrace {
    id: number;
    uuid: string;
    original_name: string;
    description: string | null;
    path: string;
    owner_id: number;
    distance_km: number | null;
    elevation_gain: number | null;
    elevation_loss: number | null;
    min_elevation: number | null;
    max_elevation: number | null;
    created_at: string;
    updated_at: string;
}

export interface ElevationPoint {
    distance_km: number;
    elevation: number | null;
    lat: number;
    lon: number;
}

export interface ElevationProfile {
    path: string;
    profile: ElevationPoint[];
}

export interface GPXTracesResponse {
    total: number;
    skip: number;
    limit: number;
    items: GPXTrace[];
}

export const gpxTraceService = {
    /**
     * Récupère les traces GPX de l'utilisateur connecté
     */
    async getUserTraces(skip = 0, limit = 20): Promise<GPXTracesResponse> {
        const response = await apiClient.get<GPXTracesResponse>(`/gpx/user/me?skip=${skip}&limit=${limit}`);
        return response;
    },

    /**
     * Récupère les métadonnées d'une trace GPX sans authentification (pour les liens partagés)
     */
    async getTraceMeta(tracePath: string): Promise<GPXTrace> {
        return apiClient.get<GPXTrace>(`/gpx/meta/${tracePath}`);
    },

    /**
     * Récupère le contenu GPX d'une trace (pour affichage sur la carte)
     * Le paramètre updatedAt est utilisé comme version pour invalider le cache navigateur.
     */
    getTraceGPXUrl(tracePath: string, updatedAt?: string): string {
        const base = `${process.env.REACT_APP_API_URL}/gpx/${tracePath}`;
        if (updatedAt) {
            const v = new Date(updatedAt).getTime();
            return `${base}?v=${v}`;
        }
        return base;
    },

    /**
     * Upload une nouvelle trace GPX
     */
    async uploadTrace(file: File, description?: string): Promise<GPXTrace> {
        const formData = new FormData();
        formData.append('file', file);
        if (description) {
            formData.append('description', description);
        }

        const response = await apiClient.postForm<GPXTrace>('/gpx/upload', formData);
        return response;
    },

    /**
     * Supprime une trace GPX
     */
    async deleteTrace(tracePath: string): Promise<void> {
        await apiClient.delete(`/gpx/${tracePath}`);
    },

    /**
     * Met à jour une trace GPX
     */
    async updateTrace(tracePath: string, updates: Partial<Pick<GPXTrace, 'original_name' | 'description'>>): Promise<GPXTrace> {
        const response = await apiClient.patch<GPXTrace>(`/gpx/${tracePath}`, updates);
        return response;
    },

    /**
     * Sauvegarde une trace dessinée (liste de coordonnées) côté serveur
     */
    async saveRouteAsTrace(name: string, coordinates: [number, number][], description?: string): Promise<GPXTrace> {
        return apiClient.post<GPXTrace>('/gpx/from-route', { name, coordinates, description });
    },

    /**
     * Met à jour les coordonnées d'une trace existante (réécrit le fichier GPX)
     */
    async updateTraceCoordinates(tracePath: string, coordinates: [number, number][]): Promise<GPXTrace> {
        return apiClient.put<GPXTrace>(`/gpx/${tracePath}/coordinates`, { coordinates });
    },

    /**
     * Récupère le profil altimétrique d'une trace
     */
    async getElevationProfile(tracePath: string): Promise<ElevationProfile> {
        return apiClient.get<ElevationProfile>(`/gpx/${tracePath}/elevation-profile`);
    },

    /**
     * Télécharge le fichier GPX d'une trace
     */
    async downloadTrace(tracePath: string, fileName?: string): Promise<void> {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/gpx/${tracePath}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
        });

        if (!response.ok) {
            throw new Error('Erreur lors du téléchargement de la trace');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || `trace-${tracePath}.gpx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
};
